import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { seoAPI } from '../../services/api';
import useDocumentTitle from '../../hooks/useDocumentTitle';

interface Keyword {
  id?: number;
  keyword: string;
  google?: { rank: number; checked_at: string } | null;
  bing?: { rank: number } | null;
  prev_google?: number | null;
}

interface Backlink {
  id: number;
  source_url: string;
  anchor_text: string;
  first_seen_at: string;
  last_seen_at: string;
}

interface BacklinkData {
  backlinks: Backlink[];
  domain_authority: number | null;
  last_checked: string | null;
  total: number;
  referring_domains: number;
}

const rankBadge = (rank: number | undefined | null) => {
  if (!rank) return <span style={{ fontSize: '0.72rem', color: '#cbd5e1' }}>—</span>;
  const bg = rank <= 3 ? '#d1fae5' : rank <= 10 ? '#dcfce7' : rank <= 30 ? '#fef3c7' : rank <= 100 ? '#f1f5f9' : '#fee2e2';
  const color = rank <= 3 ? '#065f46' : rank <= 10 ? '#15803d' : rank <= 30 ? '#b45309' : rank <= 100 ? '#475569' : '#dc2626';
  return (
    <span style={{ fontSize: '0.75rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: bg, color }}>
      #{rank}
    </span>
  );
};

const TrendIcon: React.FC<{ curr?: number | null; prev?: number | null }> = ({ curr, prev }) => {
  if (!curr || !prev) return <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>;
  const diff = prev - curr;
  if (diff > 0) return <span style={{ color: '#10b981', fontSize: 12, fontWeight: 700 }}>↑{diff}</span>;
  if (diff < 0) return <span style={{ color: '#ef4444', fontSize: 12, fontWeight: 700 }}>↓{Math.abs(diff)}</span>;
  return <span style={{ color: '#94a3b8', fontSize: 12 }}>→</span>;
};

const DomainRingScore: React.FC<{ score: number | null }> = ({ score }) => {
  if (score === null) return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '2rem', fontWeight: 800, color: '#94a3b8' }}>—</div>
      <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Not configured</div>
    </div>
  );
  const color = score >= 60 ? '#10b981' : score >= 30 ? '#f59e0b' : '#ef4444';
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '2.2rem', fontWeight: 800, color, lineHeight: 1 }}>{score}</div>
      <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 2 }}>Domain Authority</div>
      <div style={{
        marginTop: 4, fontSize: '0.65rem', padding: '2px 8px', borderRadius: 99,
        background: color + '22', color, display: 'inline-block', fontWeight: 600,
      }}>
        {score >= 60 ? 'Strong' : score >= 30 ? 'Moderate' : 'Low'}
      </div>
    </div>
  );
};

const KeywordMonitorPage: React.FC = () => {
  useDocumentTitle('Keyword Monitor');
  const { currentAsset } = useAuth();
  const assetId = currentAsset?.id;

  const [activeTab, setActiveTab] = useState<'keywords' | 'backlinks'>('keywords');

  // Keywords state
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [newKw, setNewKw] = useState('');
  const [addingKw, setAddingKw] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGroup, setFilterGroup] = useState<'all' | 'top10' | 'top30' | 'top100' | 'unranked'>('all');

  // Backlinks state
  const [backlinkData, setBacklinkData] = useState<BacklinkData | null>(null);
  const [loadingBacklinks, setLoadingBacklinks] = useState(false);
  const [checkingBacklinks, setCheckingBacklinks] = useState(false);
  const [blSearch, setBlSearch] = useState('');

  const load = useCallback(async () => {
    if (!assetId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [kwRes, rankRes] = await Promise.all([
        seoAPI.getKeywords(assetId),
        seoAPI.getRanks(assetId),
      ]);
      const kws: { keyword: string; id?: number }[] = kwRes.data?.data?.keywords || [];
      const ranks: Keyword[] = rankRes.data?.data?.ranks || [];
      const merged: Keyword[] = kws.map(k => {
        const r = ranks.find(r => r.keyword.toLowerCase() === k.keyword.toLowerCase());
        return { id: k.id, keyword: k.keyword, google: r?.google ?? null, bing: r?.bing ?? null, prev_google: r?.prev_google ?? null };
      });
      ranks.forEach(r => {
        if (!merged.find(m => m.keyword.toLowerCase() === r.keyword.toLowerCase())) {
          merged.push(r as Keyword);
        }
      });
      setKeywords(merged);
    } catch {
      toast.error('Failed to load keywords');
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  const loadBacklinks = useCallback(async () => {
    if (!assetId) return;
    setLoadingBacklinks(true);
    try {
      const res = await seoAPI.getBacklinks(assetId);
      setBacklinkData(res.data?.data ?? null);
    } catch {
      // No backlinks yet — that's OK
    } finally {
      setLoadingBacklinks(false);
    }
  }, [assetId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (activeTab === 'backlinks') loadBacklinks();
  }, [activeTab, loadBacklinks]);

  const handleCheckRanks = async () => {
    if (!assetId || checking) return;
    setChecking(true);
    try {
      await seoAPI.checkRanks(assetId);
      toast.success('Rank check complete');
      await load();
    } catch {
      toast.error('Failed to check ranks');
    } finally {
      setChecking(false);
    }
  };

  const handleCheckBacklinks = async () => {
    if (!assetId || checkingBacklinks) return;
    setCheckingBacklinks(true);
    try {
      const res = await seoAPI.checkBacklinks(assetId);
      const data = res.data?.data;
      setBacklinkData(data);
      toast.success(`Found ${data?.new_found ?? 0} referring pages`);
    } catch {
      toast.error('Failed to discover backlinks');
    } finally {
      setCheckingBacklinks(false);
    }
  };

  const handleAddKeyword = async () => {
    if (!assetId || !newKw.trim()) return;
    const toAdd = newKw.trim().split('\n').map(k => k.trim()).filter(Boolean);
    if (!toAdd.length) return;
    setAddingKw(true);
    try {
      const existing = keywords.map(k => k.keyword);
      const combined = Array.from(new Set([...existing, ...toAdd]));
      await seoAPI.saveKeywords(assetId, combined);
      toast.success(`${toAdd.length} keyword${toAdd.length > 1 ? 's' : ''} added`);
      setNewKw('');
      await load();
    } catch {
      toast.error('Failed to add keywords');
    } finally {
      setAddingKw(false);
    }
  };

  const handleRemove = async (keyword: string) => {
    if (!assetId) return;
    const updated = keywords.filter(k => k.keyword !== keyword).map(k => k.keyword);
    try {
      await seoAPI.saveKeywords(assetId, updated);
      setKeywords(prev => prev.filter(k => k.keyword !== keyword));
      toast.success('Keyword removed');
    } catch {
      toast.error('Failed to remove keyword');
    }
  };

  const filtered = keywords
    .filter(k => {
      if (searchTerm && !k.keyword.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      const r = k.google?.rank;
      if (filterGroup === 'top10') return r && r <= 10;
      if (filterGroup === 'top30') return r && r <= 30;
      if (filterGroup === 'top100') return r && r <= 100;
      if (filterGroup === 'unranked') return !r;
      return true;
    })
    .sort((a, b) => (a.google?.rank ?? 9999) - (b.google?.rank ?? 9999));

  const grouped = {
    top3: keywords.filter(k => k.google?.rank && k.google.rank <= 3).length,
    top10: keywords.filter(k => k.google?.rank && k.google.rank <= 10).length,
    top30: keywords.filter(k => k.google?.rank && k.google.rank <= 30).length,
    unranked: keywords.filter(k => !k.google?.rank).length,
  };

  const filteredBacklinks = (backlinkData?.backlinks || []).filter(b => {
    if (!blSearch) return true;
    return b.source_url.toLowerCase().includes(blSearch.toLowerCase()) ||
      b.anchor_text.toLowerCase().includes(blSearch.toLowerCase());
  });

  return (
    <DashboardLayout>
      <div className="container-fluid py-3">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
          <div>
            <h4 className="fw-bold mb-1">
              <i className="bi bi-search text-success me-2"></i>Keyword Monitor
            </h4>
            <p className="text-muted small mb-0">
              Track keyword rankings (via DuckDuckGo/Bing) &amp; backlinks · {keywords.length} keywords tracked
            </p>
          </div>
          {activeTab === 'keywords' ? (
            <button className={`btn btn-primary btn-sm ${checking ? 'disabled' : ''}`}
              onClick={handleCheckRanks} disabled={checking}>
              {checking
                ? <><span className="spinner-border spinner-border-sm me-1" />Checking…</>
                : <><i className="bi bi-arrow-clockwise me-1"></i>Check Ranks Now</>
              }
            </button>
          ) : (
            <button className={`btn btn-primary btn-sm ${checkingBacklinks ? 'disabled' : ''}`}
              onClick={handleCheckBacklinks} disabled={checkingBacklinks}>
              {checkingBacklinks
                ? <><span className="spinner-border spinner-border-sm me-1" />Discovering…</>
                : <><i className="bi bi-link-45deg me-1"></i>Find Backlinks</>
              }
            </button>
          )}
        </div>

        {/* Tabs */}
        <ul className="nav nav-pills mb-3">
          <li className="nav-item">
            <button className={`nav-link ${activeTab === 'keywords' ? 'active' : ''}`}
              onClick={() => setActiveTab('keywords')}>
              <i className="bi bi-bar-chart-line me-1"></i>Keywords
              <span className="badge ms-1" style={{
                background: activeTab === 'keywords' ? 'rgba(255,255,255,0.25)' : '#e2e8f0',
                color: activeTab === 'keywords' ? '#fff' : '#475569', fontSize: '0.65rem',
              }}>{keywords.length}</span>
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link ${activeTab === 'backlinks' ? 'active' : ''}`}
              onClick={() => setActiveTab('backlinks')}>
              <i className="bi bi-link-45deg me-1"></i>Backlinks
              {backlinkData && (
                <span className="badge ms-1" style={{
                  background: activeTab === 'backlinks' ? 'rgba(255,255,255,0.25)' : '#e2e8f0',
                  color: activeTab === 'backlinks' ? '#fff' : '#475569', fontSize: '0.65rem',
                }}>{backlinkData.total}</span>
              )}
            </button>
          </li>
        </ul>

        {/* ── KEYWORDS TAB ──────────────────────────────────────────────────── */}
        {activeTab === 'keywords' && (
          <>
            {/* Stats */}
            <div className="row g-3 mb-4">
              {[
                { label: 'Top 3', val: grouped.top3, color: '#065f46', bg: '#d1fae5', icon: 'bi-trophy-fill' },
                { label: 'Top 10', val: grouped.top10, color: '#15803d', bg: '#dcfce7', icon: 'bi-award-fill' },
                { label: 'Top 30', val: grouped.top30, color: '#b45309', bg: '#fef3c7', icon: 'bi-star-fill' },
                { label: 'Unranked', val: grouped.unranked, color: '#64748b', bg: '#f1f5f9', icon: 'bi-question-circle-fill' },
              ].map((s, i) => (
                <div key={i} className="col-6 col-md-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center gap-3 py-3">
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className={`bi ${s.icon}`} style={{ fontSize: 16, color: s.color }}></i>
                      </div>
                      <div>
                        <div style={{ fontSize: '1.4rem', fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.val}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{s.label}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="row g-3">
              {/* Keyword list */}
              <div className="col-12 col-xl-8">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white d-flex align-items-center gap-3 flex-wrap">
                    <input
                      type="text" className="form-control form-control-sm" placeholder="Filter keywords…"
                      style={{ maxWidth: 200 }} value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    />
                    <div className="btn-group btn-group-sm ms-auto">
                      {(['all', 'top10', 'top30', 'top100', 'unranked'] as const).map(g => (
                        <button key={g} className={`btn ${filterGroup === g ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setFilterGroup(g)}>
                          {g === 'all' ? 'All' : g === 'top10' ? 'Top 10' : g === 'top30' ? 'Top 30' : g === 'top100' ? 'Top 100' : 'Unranked'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="card-body p-0">
                    {loading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status"></div>
                      </div>
                    ) : filtered.length === 0 ? (
                      <div className="text-center text-muted py-5">
                        <i className="bi bi-search fs-1 d-block mb-2"></i>
                        {searchTerm ? 'No matching keywords' : 'No keywords tracked yet'}
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Keyword</th>
                              <th className="text-center">
                                Search Rank
                                <span className="ms-1 text-muted" style={{ fontSize: '0.65rem', fontWeight: 400 }}
                                  title="Approximate rank via DuckDuckGo/Bing scraping">ℹ️</span>
                              </th>
                              <th className="text-center">Trend</th>
                              <th className="text-center">Last Checked</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {filtered.map((k, i) => (
                              <tr key={i}>
                                <td>
                                  <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#0f172a' }}>{k.keyword}</span>
                                </td>
                                <td className="text-center">{rankBadge(k.google?.rank)}</td>
                                <td className="text-center">
                                  <TrendIcon curr={k.google?.rank} prev={k.prev_google} />
                                </td>
                                <td className="text-center">
                                  <small className="text-muted">
                                    {k.google?.checked_at
                                      ? new Date(k.google.checked_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                                      : '—'}
                                  </small>
                                </td>
                                <td className="text-end">
                                  <button className="btn btn-sm btn-link text-danger p-0" onClick={() => handleRemove(k.keyword)}>
                                    <i className="bi bi-trash3"></i>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Add keywords panel */}
              <div className="col-12 col-xl-4">
                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-header bg-white">
                    <h6 className="mb-0 fw-semibold"><i className="bi bi-plus-circle me-2 text-success"></i>Add Keywords</h6>
                  </div>
                  <div className="card-body">
                    <textarea
                      className="form-control mb-3"
                      rows={6}
                      placeholder={'One keyword per line:\ncloud analytics\nwebsite monitoring\nseo tools…'}
                      value={newKw}
                      onChange={e => setNewKw(e.target.value)}
                      style={{ fontSize: '0.85rem', resize: 'vertical' }}
                    />
                    <button className="btn btn-success w-100" onClick={handleAddKeyword} disabled={addingKw || !newKw.trim()}>
                      {addingKw ? <><span className="spinner-border spinner-border-sm me-1" />Adding…</> : 'Add Keywords'}
                    </button>
                  </div>
                </div>

                <div className="card border-0 bg-light">
                  <div className="card-body p-3">
                    <p className="fw-semibold small mb-2"><i className="bi bi-lightbulb me-1 text-warning"></i>How Rankings Work</p>
                    <ul className="small text-muted mb-0 ps-3">
                      <li>Rankings are checked via <strong>DuckDuckGo/Bing</strong> — no paid API needed</li>
                      <li>Results are <strong>approximate</strong> — may differ from actual Google position</li>
                      <li>If the check is blocked, rank will show as "—" (not an error)</li>
                      <li>Top 10 = likely on the first page of search results</li>
                      <li>Check ranks daily for trend tracking</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── BACKLINKS TAB ─────────────────────────────────────────────────── */}
        {activeTab === 'backlinks' && (
          <div className="row g-3">
            {/* Summary cards */}
            <div className="col-12">
              <div className="row g-3 mb-2">
                {[
                  {
                    label: 'Domain Authority',
                    content: <DomainRingScore score={backlinkData?.domain_authority ?? null} />,
                    bg: '#f0fdf4',
                  },
                  {
                    label: 'Total Backlinks',
                    content: (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '2.2rem', fontWeight: 800, color: '#3b82f6', lineHeight: 1 }}>
                          {backlinkData?.total ?? '—'}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 2 }}>Discovered</div>
                      </div>
                    ),
                    bg: '#eff6ff',
                  },
                  {
                    label: 'Referring Domains',
                    content: (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '2.2rem', fontWeight: 800, color: '#8b5cf6', lineHeight: 1 }}>
                          {backlinkData?.referring_domains ?? '—'}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 2 }}>Unique domains</div>
                      </div>
                    ),
                    bg: '#f5f3ff',
                  },
                  {
                    label: 'Last Checked',
                    content: (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: backlinkData?.last_checked ? '0.85rem' : '1.2rem', fontWeight: 700, color: '#64748b' }}>
                          {backlinkData?.last_checked
                            ? new Date(backlinkData.last_checked).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
                            : '—'}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 2 }}>
                          {backlinkData?.last_checked
                            ? new Date(backlinkData.last_checked).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                            : 'Never checked'}
                        </div>
                      </div>
                    ),
                    bg: '#f8fafc',
                  },
                ].map((card, i) => (
                  <div key={i} className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm h-100" style={{ background: card.bg }}>
                      <div className="card-body d-flex align-items-center justify-content-center py-3">
                        {card.content}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Backlinks table */}
            <div className="col-12 col-xl-8">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white d-flex align-items-center gap-3 flex-wrap">
                  <input
                    type="text" className="form-control form-control-sm" placeholder="Search URLs or anchor text…"
                    style={{ maxWidth: 260 }} value={blSearch} onChange={e => setBlSearch(e.target.value)}
                  />
                  <span className="text-muted small ms-auto">{filteredBacklinks.length} results</span>
                </div>
                <div className="card-body p-0">
                  {loadingBacklinks ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status"></div>
                    </div>
                  ) : filteredBacklinks.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      <i className="bi bi-link-45deg fs-1 d-block mb-2 opacity-25"></i>
                      <p className="mb-1 fw-semibold">No backlinks discovered yet</p>
                      <small>Click "Find Backlinks" to discover pages linking to your site</small>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Source URL</th>
                            <th>Anchor Text</th>
                            <th className="text-center">First Seen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredBacklinks.map((bl, i) => {
                            let domain = '';
                            try { domain = new URL(bl.source_url).hostname; } catch { domain = bl.source_url; }
                            return (
                              <tr key={i}>
                                <td style={{ maxWidth: 320 }}>
                                  <a href={bl.source_url} target="_blank" rel="noopener noreferrer"
                                    style={{ fontSize: '0.8rem', color: '#3b82f6', wordBreak: 'break-all' }}>
                                    <i className="bi bi-box-arrow-up-right me-1" style={{ fontSize: '0.7rem' }}></i>
                                    {domain}
                                  </a>
                                  <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 1, wordBreak: 'break-all' }}>
                                    {bl.source_url.replace(/^https?:\/\//, '')}
                                  </div>
                                </td>
                                <td style={{ maxWidth: 200 }}>
                                  <span style={{ fontSize: '0.8rem', color: '#334155' }}>
                                    {bl.anchor_text || <span className="text-muted fst-italic">no anchor</span>}
                                  </span>
                                </td>
                                <td className="text-center">
                                  <small className="text-muted">
                                    {new Date(bl.first_seen_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                  </small>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Info panel */}
            <div className="col-12 col-xl-4">
              <div className="card border-0 shadow-sm mb-3">
                <div className="card-header bg-white">
                  <h6 className="mb-0 fw-semibold"><i className="bi bi-info-circle me-2 text-info"></i>How it works</h6>
                </div>
                <div className="card-body">
                  <p className="small text-muted mb-3">
                    Backlinks are discovered by searching Bing and DuckDuckGo for pages that link to your domain.
                  </p>
                  <div className="d-flex flex-column gap-2">
                    {[
                      { icon: 'bi-search', text: 'Bing link: search', status: 'Used automatically', color: '#3b82f6' },
                      { icon: 'bi-globe', text: 'DuckDuckGo', status: 'Fallback', color: '#10b981' },
                      { icon: 'bi-graph-up', text: 'Domain Authority', status: process.env.REACT_APP_HAS_OPR ? 'Active' : 'Set OPEN_PAGE_RANK_API_KEY', color: '#8b5cf6' },
                    ].map((item, i) => (
                      <div key={i} className="d-flex align-items-center gap-2">
                        <div style={{ width: 28, height: 28, borderRadius: 6, background: item.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <i className={`bi ${item.icon}`} style={{ fontSize: 12, color: item.color }}></i>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#0f172a' }}>{item.text}</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{item.status}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="card border-0 bg-light">
                <div className="card-body p-3">
                  <p className="fw-semibold small mb-2"><i className="bi bi-lightbulb me-1 text-warning"></i>Improve accuracy</p>
                  <ul className="small text-muted mb-0 ps-3">
                    <li>Set <code>BING_SEARCH_API_KEY</code> for rank tracking</li>
                    <li>Set <code>OPEN_PAGE_RANK_API_KEY</code> for domain authority (free at openpagerank.com)</li>
                    <li>Run "Find Backlinks" weekly to track growth</li>
                    <li>More backlinks from quality sites = higher DA</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default KeywordMonitorPage;
